import type { CarouselImageProperty } from './ProductImage';
import type { ProductVideoProps } from './ProductVideo';
import type { MouseEvent } from 'react';

import dynamic from 'next/dynamic';
import styled from 'styled-components';

import { ProductImage } from './ProductImage';
import type { ProductVideo as ProductVideoSchema } from '~/amplienceTypes/schemas/imported/product-video-schema';
import { MOBILE_VIDEO_POSITION } from '~/utilities/constants';
import { useGalleryContext } from '~/utilities/context/dynamic/GalleryContext';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import type { Maybe, Video } from '~/utilities/graphql/codegen';

const ProductVideo = dynamic<ProductVideoProps>(
  () => import('./ProductVideo').then(mod => mod.ProductVideo),
  { ssr: false }
);

const S = {
  GridCarouselInnerItem: styled.li`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;

    &:first-child {
      transform: translateX(0);
    }

    &:where([data-is-swipe-mode='true'] &) {
      transform: unset !important;
      position: relative;
      scroll-snap-align: start;
      scroll-snap-stop: always;
      min-width: 90vw;
      overflow: hidden;
      background-color: ${({ theme }) => theme.colors.neutral[200]};
    }

    &:where([data-is-swipe-mode='false'] &) {
      overflow: hidden;
      position: absolute;
      transform: translateX(calc(100% + var(--scrollbar-width)));
    }
  `,
};

interface CarouselItemProps {
  productImage: CarouselImageProperty;
  styleVariant: CarouselImageProperty | null;
  index: number;
  currentImageIndex: number;
  isSwipeMode: boolean;
  setZoomPanelScrollXCenter: (e: MouseEvent) => void;
  itemRef: (el: HTMLLIElement | null) => void;
  video?: Maybe<Video>;
  videoActive: boolean;
  setVideoActive: (active: boolean) => void;
  autoplay: boolean;
  productVideoContent?: ProductVideoSchema;
}

export const CarouselItem = ({
  productImage,
  styleVariant,
  index,
  currentImageIndex,
  isSwipeMode,
  setZoomPanelScrollXCenter,
  itemRef,
  video,
  videoActive,
  setVideoActive,
  autoplay,
  productVideoContent,
}: CarouselItemProps) => {
  const { isDesktop } = useMediaQueryContext();
  const { galleryZoomIn } = useGalleryContext();

  const isVideoSlide = !isDesktop && video && index === MOBILE_VIDEO_POSITION;

  return (
    <S.GridCarouselInnerItem
      className="gallery-item"
      data-testid="image-hero"
      onClick={setZoomPanelScrollXCenter}
      ref={itemRef}
    >
      {isVideoSlide ? (
        <ProductVideo
          {...(productVideoContent as ProductVideoSchema)}
          active={videoActive}
          setActive={setVideoActive}
          autoplay={autoplay}
          isInUserView={currentImageIndex === index}
          video={video}
          isSwipeMode={isSwipeMode && !galleryZoomIn}
        />
      ) : (
        <ProductImage
          lazyLoad={!isSwipeMode && index !== currentImageIndex}
          {...(styleVariant || productImage)}
        />
      )}
    </S.GridCarouselInnerItem>
  );
};
